import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { C, T } from './leadOverviewCardTokens';
import { OverviewCardTitleWithIcon } from './overviewCardPrimitives';
import { abbreviateLongMonthNamesInString, stripClockHourLeadingZero } from './overviewFormatUtils';
import { temperatureIntentDisplayLabel } from './leadTemperatureUtils';
import {
  conversionIntentChipStyle,
  normalizeLeadConversionForCard,
  overviewNaTypographySx,
} from './leadCoverageUtils';

const LEAD_CONVERSION_EMPTY_DESCRIPTION =
  'Conversion score, intents, and sentiment show here after a call is assessed with AI.';

export default function LeadCoverage({
  coveragePct,
  leadConversion,
  veryStaleClosureHint,
  sentimentAndTopics,
  /** Shown under N/A when `leadConversion` is null */
  emptyConversionDescription = LEAD_CONVERSION_EMPTY_DESCRIPTION,
}) {
  if (leadConversion == null) {
    return (
      <Box
        sx={{
          bgcolor: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: '14px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          p: '18px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 1.25 }}>
          <OverviewCardTitleWithIcon icon="📊" iconBg="#E0E7FF" title="Conversion probability" />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 120,
            gap: 1.5,
            textAlign: 'center',
          }}
        >
          <Typography sx={overviewNaTypographySx}>N/A</Typography>
          {emptyConversionDescription ? (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: C.slate3,
                lineHeight: 1.55,
                maxWidth: 320,
                px: 0.5,
              }}
            >
              {emptyConversionDescription}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  }

  const nv = normalizeLeadConversionForCard(leadConversion);
  const heroPct = nv?.leadScore != null ? nv.leadScore : Math.round(coveragePct);
  const domLabel =
    nv?.dominantIntent != null && String(nv.dominantIntent).trim() !== ''
      ? temperatureIntentDisplayLabel(nv.dominantIntent)
      : '';
  const latestLabel =
    nv?.latestIntent != null && String(nv.latestIntent).trim() !== '' ? temperatureIntentDisplayLabel(nv.latestIntent) : '';
  const domPal = conversionIntentChipStyle(nv?.dominantIntent);
  const latestPal = conversionIntentChipStyle(nv?.latestIntent);

  let avgSent = nv?.sentimentScore != null ? nv.sentimentScore : null;
  if (avgSent == null && sentimentAndTopics?.breakdown?.total) {
    const b = sentimentAndTopics.breakdown;
    const t = b.total || 1;
    avgSent = Math.round((Number(b.positive || 0) * 88 + Number(b.neutral || 0) * 58 + Number(b.negative || 0) * 28) / t);
  }

  const assessedLine = nv?.assessedAt
    ? stripClockHourLeadingZero(abbreviateLongMonthNamesInString(String(nv.assessedAt)))
    : null;
  const shortNoteLine = String(nv?.shortNote ?? '').trim();

  return (
    <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', p: '18px', height: '100%' }}>
      <Box sx={{ mb: 1.25 }}>
        <OverviewCardTitleWithIcon icon="📊" iconBg="#E0E7FF" title="Conversion probability" />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ ...T.heroMetric, color: C.scoreBrown }}>{heroPct}%</Typography>
      </Box>

      <Box sx={{ borderTop: `1px solid ${C.borderSubtle}`, pt: 1.25, display: 'grid', gap: 1.15 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: C.slate3 }}>Dominant intent</Typography>
          {domLabel ? (
            <Chip
              label={domLabel}
              size="small"
              sx={{ height: 26, fontSize: '0.7rem', fontWeight: 800, bgcolor: domPal.bg, color: domPal.fg, border: `1px solid ${domPal.bdr}` }}
            />
          ) : (
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: C.slate4 }}>—</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: C.slate3 }}>Latest intent</Typography>
          {latestLabel ? (
            <Chip
              label={latestLabel}
              size="small"
              sx={{ height: 26, fontSize: '0.7rem', fontWeight: 800, bgcolor: latestPal.bg, color: latestPal.fg, border: `1px solid ${latestPal.bdr}` }}
            />
          ) : (
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: C.slate4 }}>—</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: C.slate3 }}>Avg sentiment</Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 800, color: C.slate2 }}>{avgSent != null ? `${avgSent} / 100` : '—'}</Typography>
        </Box>
      </Box>

      {assessedLine || shortNoteLine ? (
        <Box sx={{ borderTop: `1px solid ${C.borderSubtle}`, mt: 1.25, pt: 1.25, display: 'grid', gap: shortNoteLine ? 1 : 0 }}>
          {assessedLine ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: C.slate3 }}>Last Call</Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 800, color: C.slate2 }}>{assessedLine}</Typography>
            </Box>
          ) : shortNoteLine ? (
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: C.slate3 }}>Last Call</Typography>
          ) : null}
          {shortNoteLine ? (
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: C.slate3, lineHeight: 1.55, fontStyle: 'italic' }}>{shortNoteLine}</Typography>
          ) : null}
        </Box>
      ) : null}

      {veryStaleClosureHint ? (
        <Typography sx={{ ...T.bodyPrimary, color: C.redDark, mt: 1, fontSize: '11px', fontWeight: 600 }}>
          Stale with low intent — consider one final touch before close-out.
        </Typography>
      ) : null}
    </Box>
  );
}
